import { Icon } from '@dc-shared-web/components/base/Icon'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dc-shared-web/components/shadcn/dropdown-menu'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { cva, VariantProps } from 'class-variance-authority'
import { FC, forwardRef, PropsWithChildren, ReactNode } from 'react'
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'
import { faCheck } from '@document-crunch/pro-regular-svg-icons'
import { cn } from '@dc-shared-web/lib'
import { DropdownMenuContentProps } from '@radix-ui/react-dropdown-menu'

export type DropdownProps = PropsWithChildren<{
  /** Alignment of the dropdown content. Useful when the dropdown is used at the edge of the screen. */
  contentAlign?: DropdownMenuContentProps['align']
  /** Element to be used as a trigger. Make sure the element forwards the ref. */
  trigger: ReactNode
  /** If true, the dropdown will use the width of the content. */
  useContentWidth?: boolean
}>

export const Dropdown: FC<DropdownProps> = ({
  contentAlign = 'center',
  trigger,
  useContentWidth,
  children,
}) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={contentAlign}
        className={cn({ 'tw-min-w-[unset]': useContentWidth })}
      >
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

Dropdown.displayName = 'Dropdown'

const dropdownItemStyles = cva(
  'tw-flex tw-items-center tw-gap-2 tw-h-8 tw-text-neutral-700 [&_.themed-icon]:tw-text-neutral-700 [&_.themed-icon]:tw-w-4 focus:tw-text-neutral-700',
  {
    variants: {
      color: {
        default: 'hover:tw-bg-neutral-100 focus:tw-bg-neutral-100',
        blue: '[&_.themed-icon]:tw-text-blue-700 hover:tw-bg-blue-100 focus:tw-bg-blue-100',
        yellow:
          '[&_.themed-icon]:tw-text-yellow-700 hover:tw-bg-yellow-100 focus:tw-bg-yellow-100',
        green:
          '[&_.themed-icon]:tw-text-green-700 hover:tw-bg-green-100 focus:tw-bg-green-100',
        red: '[&_.themed-icon]:tw-text-red-700 hover:tw-bg-red-100 focus:tw-bg-red-100',
      },
    },
    defaultVariants: {
      color: 'default',
    },
  }
)

export type DropdownItemProps = React.ComponentPropsWithoutRef<
  typeof DropdownMenuPrimitive.Item
> &
  VariantProps<typeof dropdownItemStyles> & {
    color?: 'default' | 'blue' | 'yellow' | 'green' | 'red'
    /** Whether the item is disabled. */
    disabled?: boolean
    /** Icon to be displayed on the left side of the item. It gets adapted to match the color of the item if needed. */
    icon?: IconDefinition
    /** Whether the item is selected. If true, a check icon is displayed on the right side of the item. */
    selected?: boolean
  }

export const DropdownItem = forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitive.Item>,
  DropdownItemProps
>(({ children, color, disabled, icon, selected, ...rest }, ref) => {
  return (
    <DropdownMenuItem
      ref={ref}
      disabled={disabled}
      className={dropdownItemStyles({ color })}
      {...rest}
    >
      {/* themed-icon class is used to style the icon in dropdownItemStyles */}
      {icon && (
        <Icon
          icon={icon}
          size='sm'
          className='themed-icon'
          aria-hidden='true'
        />
      )}
      {children && <div className='tw-flex-1'>{children}</div>}
      {selected && <Icon icon={faCheck} size='sm' aria-hidden='true' />}
    </DropdownMenuItem>
  )
})

DropdownItem.displayName = 'DropdownItem'

export const DropdownDivider: FC = () => {
  return <DropdownMenuSeparator />
}
